// 圖片儲存工具
//
// 為什麼需要這支：iOS 的 <a download> 只會把檔案存進「檔案」App，不會進照片庫。
// 想讓圖片落在照片庫，Web 端唯一的途徑是 Web Share API —— 使用者點「儲存圖片」後
// 跳出系統分享表單，再自己點一次「儲存影像」。純 Web 做不到零額外步驟直接寫入照片庫，
// 這是 iOS 沙箱限制，不是實作沒做好。
//
// Android 與桌機不走這條：它們的 <a download> 本來就一鍵落地、沒有額外選單，
// 換成分享表單反而是退步，所以這裡做平台分流而非單純的能力偵測。

/** 分享結果。fallback 代表不支援或失敗，呼叫端須自行決定要降級成什麼 */
export type ShareOutcome = 'ok' | 'cancelled' | 'fallback';

/** 儲存結果。cancelled 是使用者在分享表單上按取消，屬正常結束而非錯誤 */
export type SaveResult = 'shared' | 'downloaded' | 'cancelled';

/** 這台裝置是不是 iOS（iPhone / iPad / iPod） */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iP(hone|od|ad)/.test(navigator.userAgent)) return true;
  // iPadOS 13 起預設以桌機模式上網，UA 會偽裝成 Macintosh，只能靠觸控點數
  // 區分真 Mac 與 iPad。navigator.platform 雖已標記 deprecated，
  // 但目前沒有可靠的替代方案，先留著並附上這段說明。
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * 這台裝置按下儲存後會跳系統分享表單（而非直接下載）。
 * 供 UI 決定按鈕文案用 —— iOS 上該寫「儲存圖片」而不是「下載」，
 * 否則彈出來是分享表單會讓人以為點錯。
 *
 * 注意這裡只做平台與 API 層級的粗略判斷：要確認某個檔案能不能分享
 * 得有實際的 File，而畫面 render 時還沒有。精確判斷留給 shareFile()。
 */
export function canSaveToPhotos(): boolean {
  return isIOS() && typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** 這個檔案能不能交給系統分享表單 */
function canShareFile(file: File): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  try {
    // 舊版 iOS Safari（< 15）有 share 但沒有 canShare，也不支援分享檔案。
    // 這時 canShare?.() 回傳 undefined，必須當成不支援，
    // 所以是 === true 而不是 truthy 判斷。
    return navigator.canShare?.({ files: [file] }) === true;
  } catch {
    return false;
  }
}

/**
 * 開啟系統分享表單。與 saveImage 共用，但語意不同：
 * 這支只負責呼叫與錯誤分類，降級策略由呼叫端決定
 *（儲存情境降級成下載，分享情境降級成分享連結）。
 *
 * 呼叫前 blob 必須已經在手上。iOS 要求 share() 在 user activation 有效期內呼叫，
 * 中間夾一次網路 fetch 就可能過期，拿到 NotAllowedError。
 */
export async function shareFile(file: File, title?: string): Promise<ShareOutcome> {
  if (!canShareFile(file)) return 'fallback';

  const data: ShareData = { files: [file] };
  if (title) data.title = title;

  try {
    await navigator.share(data);
    // 注意：resolve 只代表「使用者沒取消」。Web Share API 規格上無法得知
    // 使用者實際選了哪個動作，所以 UI 不能顯示「已儲存到照片庫」這種斷言。
    return 'ok';
  } catch (error) {
    // 使用者在表單上按取消。這是正常結束，呼叫端不可因此降級或報錯 ——
    // 明明取消了卻被塞一個檔案是最糟的體驗。
    if (error instanceof Error && error.name === 'AbortError') return 'cancelled';
    // 其餘多半是 NotAllowedError（user activation 已過期）。
    return 'fallback';
  }
}

/** 建一次性的 objectURL 觸發下載，用完立刻回收 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * 把圖片交到使用者手上，用當前平台最好的方式。
 *
 * 對呼叫端承諾永不 reject：所有失敗路徑一律降級為下載，
 * 所以最壞情況也只是回到改版前的行為，不會變成點了沒反應。
 *
 * blob 必須是呼叫端已備妥的 —— 不要在這支裡面才去 fetch，
 * 那會讓 iOS 的 user activation 過期。需要抓原圖的畫面請搭配 useImageBlobPrefetch。
 */
export async function saveImage(blob: Blob, filename: string, title?: string): Promise<SaveResult> {
  // 包成 File：canShare / share 都要求 File 而非裸 Blob，而且 iOS 會依 type
  // 決定分享表單裡有沒有「儲存影像」—— type 是空字串會讓那個選項直接消失。
  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

  if (isIOS()) {
    const outcome = await shareFile(file, title);
    if (outcome === 'ok') return 'shared';
    if (outcome === 'cancelled') return 'cancelled';
    // fallback 落到下面的下載
  }

  downloadBlob(blob, filename);
  return 'downloaded';
}
